/**
 * SITE-EVIDENCE SUGGESTIONS — evidence already in the site model, projected
 * into USER-APPLIED suggestions for Generation & Impact scenario inputs.
 *
 * Rules (never guess engineering values):
 *  - A suggestion is only offered when real evidence backs it (SED climate,
 *    receptor site layers). No evidence → no suggestion, never a fallback.
 *  - Applying is always an explicit user action that writes the value AND a
 *    provenance assumption row; nothing here writes silently.
 *  - The ambient-derate suggestion is the ONE suggestion that changes solver
 *    inputs. It uses cited screening slopes against the technology archetype
 *    reference and is clearly labeled to be replaced by the OEM ambient
 *    correction curve. It is suppressed while an equipment envelope (catalog
 *    or user) supplies the duty — the envelope's screening factor is the
 *    authority there.
 */
import type {
  AssumptionSource,
  GenerationTechnology,
} from '../../types/generationImpact';
import type { SiteEntity, SiteLayer } from '../../types/site';
import type { SiteDocument } from '../../types/siteRag';
import type { SiteEvidenceDocument } from '../../types/siteEvidence';
import { haversineMeters } from '../siteBoundaryGeometry';

export interface GenerationEvidenceSuggestion {
  field: 'ambientFactor' | 'nearestReceptorDistanceM';
  value: number;
  source: AssumptionSource;
  label: string;
  rationale: string;
  citation: string;
  confidence: 'low' | 'medium';
}

/**
 * Rating reference for gas turbines: ISO 2314 rates at a 15 °C compressor
 * inlet, and output falls essentially linearly above it.
 */
const ISO_2314_TURBINE_REFERENCE_TEMP_C = 15;

/**
 * Rating reference for reciprocating engines: ISO 3046 rates at 25 °C ambient,
 * NOT the turbines' 15 °C. A lean-burn engine holds its rated output up to
 * that point — the "derate knee" — because charge-air cooling absorbs the
 * difference, and only falls above it.
 */
const ISO_3046_ENGINE_REFERENCE_TEMP_C = 25;

const AMBIENT_FACTOR_FLOOR = 0.75;

/**
 * Screening output-derate slopes vs ambient temperature, per technology family.
 * Published screening figures — NOT project curves; the OEM ambient correction
 * curve replaces them.
 *
 * `referenceTempC` is the temperature the family is RATED at, and therefore the
 * temperature at which derating begins. It is per-technology rather than one
 * shared constant because the two families are rated against different
 * standards: ISO 2314 (15 °C) for gas turbines, ISO 3046 (25 °C) for
 * reciprocating engines. Deriving an engine derate from 15 °C double-counts the
 * 10 °C the engine is already rated to absorb and systematically UNDER-states
 * available capacity — on a 41.4 °C design dry bulb that is a 3 percentage-point
 * error in the ambient factor, which flows straight into net MW and the N-1
 * reserve verdict.
 *
 * Sources: simple-cycle ≈0.5–0.7 %/°C and combined-cycle ≈0.3–0.5 %/°C are
 * standard gas-turbine screening ranges (e.g. Brooks, "GE Gas Turbine
 * Performance Characteristics", GER-3567H); reciprocating engines are much
 * flatter (≈0.3 %/°C screening allowance) above their ISO 3046 knee.
 */
export const SCREENING_DERATE_SLOPES: Partial<Record<GenerationTechnology, {
  slopePctPerDegC: number;
  /** Rating reference — derating starts here, not at a shared constant. */
  referenceTempC: number;
  citation: string;
}>> = {
  'simple-cycle-gas-turbine': {
    slopePctPerDegC: 0.6,
    referenceTempC: ISO_2314_TURBINE_REFERENCE_TEMP_C,
    citation: 'GER-3567H screening range for heavy-duty simple-cycle output vs inlet temperature (≈0.5–0.7 %/°C above the ISO 2314 15 °C reference).',
  },
  'combined-cycle-block': {
    slopePctPerDegC: 0.4,
    referenceTempC: ISO_2314_TURBINE_REFERENCE_TEMP_C,
    citation: 'GER-3567H screening range for combined-cycle output vs inlet temperature (≈0.3–0.5 %/°C above the ISO 2314 15 °C reference).',
  },
  'reciprocating-engine': {
    slopePctPerDegC: 0.3,
    referenceTempC: ISO_3046_ENGINE_REFERENCE_TEMP_C,
    citation: 'Lean-burn engine screening allowance; engines hold rated output to the ISO 3046 25 °C derate knee, then fall ≈0.3 %/°C.',
  },
};

/**
 * Suggest an ambient output factor from the SED design dry-bulb temperature.
 * Returns null without SED climate evidence, for non-gas technologies, or when
 * the SED peak is at/below the technology's own rating reference — an engine at
 * 20 °C is still inside its ISO 3046 rating and has no derate to suggest, even
 * though that temperature is above the turbines' 15 °C reference.
 */
export function suggestAmbientFactorFromSed(
  sed: SiteEvidenceDocument | null | undefined,
  technology: GenerationTechnology,
  referenceAmbientFactor: number,
): GenerationEvidenceSuggestion | null {
  const peakTempC = sed?.climate?.data?.peakTemp;
  const slope = SCREENING_DERATE_SLOPES[technology];
  if (peakTempC == null || !Number.isFinite(peakTempC) || !slope) return null;
  if (peakTempC <= slope.referenceTempC) return null;
  if (!Number.isFinite(referenceAmbientFactor)
    || referenceAmbientFactor <= 0
    || referenceAmbientFactor > 1) return null;
  const degreesAboveReference = peakTempC - slope.referenceTempC;
  const derated = referenceAmbientFactor
    - (slope.slopePctPerDegC / 100) * degreesAboveReference;
  const value = Math.max(AMBIENT_FACTOR_FLOOR, Math.min(referenceAmbientFactor, derated));
  return {
    field: 'ambientFactor',
    value,
    source: 'site-evidence',
    label: `SED-climate screening derate — ${(value * 100).toFixed(1)}% at ${peakTempC.toFixed(1)} °C design dry bulb`,
    rationale: `The SED design dry bulb is ${peakTempC.toFixed(1)} °C, ${degreesAboveReference.toFixed(1)} °C above the ${slope.referenceTempC} °C rating reference for this technology. At the ${slope.slopePctPerDegC.toFixed(1)} %/°C screening slope the ${(referenceAmbientFactor * 100).toFixed(1)}% planning factor derates to ${(value * 100).toFixed(1)}%. Replace with the OEM ambient correction curve.`,
    citation: `SED climate design basis · ${slope.citation}`,
    confidence: 'low',
  };
}

function layerPointDistanceM(
  layer: SiteLayer,
  anchor: { lat: number; lng: number },
): number | null {
  const distances = layer.geometry.coordinates
    .map(coordinate => haversineMeters(anchor, coordinate))
    .filter(distance => Number.isFinite(distance) && distance > 0);
  if (distances.length === 0) return null;
  return Math.min(...distances);
}

/**
 * Suggest the nearest-receptor distance from INDEPENDENTLY documented
 * `impact.receptor` site layers. Parcel edges are NOT receptors — no receptor
 * layer means no suggestion, never a boundary fallback.
 *
 * `scenario-derived` layers are excluded, and that exclusion is the point.
 * Publishing a Generation & Impact layout writes every `scenario.input.receptors`
 * entry BACK to the site as an `impact.receptor` layer, so without this filter
 * the "site evidence" this reads is the scenario's own previously-entered value
 * — a suggestion derived from itself, presented as independent corroboration.
 * Only a hand-drawn or imported receptor is real evidence here.
 */
export function suggestNearestReceptorDistance(
  site: SiteEntity,
  anchor: { lat: number; lng: number } | null | undefined,
): GenerationEvidenceSuggestion | null {
  if (!anchor) return null;
  const receptorLayers = (site.siteLayers ?? [])
    .filter(layer => layer.category === 'impact.receptor' && layer.source !== 'scenario-derived');
  let nearest: { layer: SiteLayer; distanceM: number } | null = null;
  for (const layer of receptorLayers) {
    const distanceM = layerPointDistanceM(layer, anchor);
    if (distanceM != null && (nearest == null || distanceM < nearest.distanceM)) {
      nearest = { layer, distanceM };
    }
  }
  if (!nearest) return null;
  return {
    field: 'nearestReceptorDistanceM',
    value: nearest.distanceM,
    source: 'site-evidence',
    label: `${Math.round(nearest.distanceM).toLocaleString('en-US')} m to “${nearest.layer.name}” (mapped receptor layer)`,
    rationale: `Nearest documented receptor feature “${nearest.layer.name}” is ${Math.round(nearest.distanceM).toLocaleString('en-US')} m from the layout anchor. Screening distance only — confirm the receptor location and applicable ordinance threshold.`,
    citation: `Site layer “${nearest.layer.name}” · imported ${nearest.layer.importedAt}`,
    confidence: 'medium',
  };
}

export interface ResourceLimitDocumentCandidate {
  documentId: string;
  name: string;
}

const RESOURCE_LIMIT_DOCUMENT_PATTERNS: Record<'gas' | 'water' | 'co2', RegExp> = {
  gas: /gas|pipeline|nomination|midstream|interconnect/i,
  water: /water|utility|will[- ]serve|aquifer|groundwater/i,
  co2: /co2|co₂|carbon|sequestration|class vi|offtake/i,
};

/**
 * Keyword-screened linked documents offered as PROVENANCE HINTS beside the
 * gas/water/CO₂ limit fields. Values stay manual — a filename match is never
 * a number.
 */
export function findResourceLimitDocumentCandidates(
  documents: readonly SiteDocument[],
): Readonly<Record<'gas' | 'water' | 'co2', readonly ResourceLimitDocumentCandidate[]>> {
  const match = (pattern: RegExp): ResourceLimitDocumentCandidate[] => documents
    .filter(document => pattern.test(document.fileName ?? ''))
    .slice(0, 3)
    .map(document => ({ documentId: document.id, name: document.fileName || document.id }));
  return {
    gas: match(RESOURCE_LIMIT_DOCUMENT_PATTERNS.gas),
    water: match(RESOURCE_LIMIT_DOCUMENT_PATTERNS.water),
    co2: match(RESOURCE_LIMIT_DOCUMENT_PATTERNS.co2),
  };
}
